import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  approveSkill,
  deleteSkill,
  getPendingSkills,
  getPendingSkillsCount,
  rejectSkill,
} from '../services/skillApprovalService';
import type {
  SkillApprovalFiltersState,
  SkillApprovalQuery,
  SkillApprovalRow,
  SkillApprovalSortColumn,
  SkillApprovalSortDirection,
} from '../types/admin';

export const defaultSkillApprovalFilters: SkillApprovalFiltersState = {
  approvalStatus: '',
  submittedFrom: '',
  submittedTo: '',
};

const getErrorMessage = (error: any) => {
  const code = error?.code ? `${error.code}: ` : '';
  return `${code}${error?.message || 'Unable to process skill request.'}`;
};

export const useSkillApprovals = () => {
  const [rows, setRows] = useState<SkillApprovalRow[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<SkillApprovalSortColumn>('SubmittedDate');
  const [sortDirection, setSortDirection] = useState<SkillApprovalSortDirection>('Descending');
  const [filters, setFilters] = useState<SkillApprovalFiltersState>(defaultSkillApprovalFilters);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPageNumber(1);
    }, 500);

    return () => window.clearTimeout(timer);
  }, [search]);

  const query: SkillApprovalQuery = useMemo(() => ({
    pageNumber,
    pageSize,
    search: debouncedSearch,
    sortColumn,
    sortDirection,
    filters,
  }), [debouncedSearch, filters, pageNumber, pageSize, sortColumn, sortDirection]);

  const refresh = useCallback(async (nextQuery = query) => {
    setError(null);
    setIsFetching(true);
    if (!hasLoadedRef.current) setLoading(true);

    try {
      const result = await getPendingSkills(nextQuery);
      setRows(result.data);
      setRecordsTotal(result.recordsTotal);
      setRecordsFiltered(result.recordsFiltered);
      const count = await getPendingSkillsCount();
      setPendingCount(count);
      return result;
    } catch (err: any) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
      setIsFetching(false);
      hasLoadedRef.current = true;
    }
  }, [query]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const applyFilters = useCallback((nextFilters: SkillApprovalFiltersState) => {
    setFilters(nextFilters);
    setPageNumber(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultSkillApprovalFilters);
    setPageNumber(1);
  }, []);

  const changeSort = useCallback((column: SkillApprovalSortColumn) => {
    setPageNumber(1);
    setSortColumn((currentColumn) => {
      if (currentColumn !== column) {
        setSortDirection('Ascending');
        return column;
      }

      setSortDirection((currentDirection) =>
        currentDirection === 'Ascending' ? 'Descending' : 'Ascending'
      );
      return currentColumn;
    });
  }, []);

  const changePageSize = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPageNumber(1);
  }, []);

  const approve = useCallback(async (row: SkillApprovalRow) => {
    setActionLoadingKey(String(row.userSkillId));
    setError(null);

    try {
      await approveSkill({ userSkillId: row.userSkillId });
      await refresh();
    } catch (err: any) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setActionLoadingKey(null);
    }
  }, [refresh]);

  const reject = useCallback(async (row: SkillApprovalRow, rejectionReason: string) => {
    setActionLoadingKey(String(row.userSkillId));
    setError(null);

    try {
      await rejectSkill({ userSkillId: row.userSkillId, rejectionReason });
      await refresh();
    } catch (err: any) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setActionLoadingKey(null);
    }
  }, [refresh]);

  const remove = useCallback(async (row: SkillApprovalRow) => {
    setActionLoadingKey(String(row.userSkillId));
    setError(null);

    try {
      await deleteSkill(row.userSkillId);
      await refresh();
    } catch (err: any) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setActionLoadingKey(null);
    }
  }, [refresh]);

  return {
    rows,
    recordsTotal,
    recordsFiltered,
    pendingCount,
    pageNumber,
    pageSize,
    search,
    sortColumn,
    sortDirection,
    filters,
    loading,
    isFetching,
    actionLoadingKey,
    error,
    setSearch,
    setPageNumber,
    changePageSize,
    changeSort,
    applyFilters,
    resetFilters,
    refresh,
    approve,
    reject,
    remove,
  };
};
